import React, { useState, useEffect } from 'react';
import axios from 'axios';

function SheetConfigUpdater() {
  const [newSheetId, setNewSheetId] = useState('');
  const [newTabName, setNewTabName] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedTabName, setSuggestedTabName] = useState('');
  const [currentConfig, setCurrentConfig] = useState({ sheetId: '', tabName: '' });

  // Load suggested tab name when component mounts
  useEffect(() => {
    const fetchSuggestion = async () => {
      try {
        const response = await axios.get('/api/suggest-current-month-tab', { withCredentials: true });
        if (response.data.success) {
          setSuggestedTabName(response.data.suggestedTabName);
          setCurrentConfig({
            sheetId: response.data.currentConfig.sheetId,
            tabName: response.data.currentConfig.tabName
          });
          
          // Pre-populate the new tab name field with the suggestion
          setNewTabName(response.data.suggestedTabName);
        }
      } catch (err) {
        console.error('Failed to get suggested tab name:', err);
      }
    };
    
    fetchSuggestion();
  }, []);

  const handleUpdateConfig = async (e) => {
    e.preventDefault();
    
    if (!newSheetId || !newTabName) {
      setMessage('Please enter both Sheet ID and Tab Name');
      return;
    }
    
    // Validate Google Sheet ID format
    if (!/^[a-zA-Z0-9_-]{25,}$/.test(newSheetId)) {
      setMessage('Invalid Google Sheet ID format. Please enter a valid ID.');
      return;
    }
    
    // Add confirmation dialog
    const confirmMessage = `Are you sure you want to update the sheet configuration to:\nSheet ID: ${newSheetId}\nTab Name: ${newTabName}?\n\nThis will change where new entries are saved.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post('/api/update-sheet-config', {
        newSheetId,
        newTabName
      }, { withCredentials: true });
      
      if (response.data.success) {
        setMessage(response.data.message);
        // Update current config display
        setCurrentConfig({
          sheetId: newSheetId,
          tabName: newTabName
        });
        
        // Clear form after successful update
        setNewSheetId('');
        setNewTabName(''); // Also clear tab name
      } else {
        setMessage(`Update failed: ${response.data.message}`);
      }
    } catch (err) {
      setMessage(`Error updating configuration: ${err.response?.data?.message || err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutoFillTabName = () => {
    setNewTabName(suggestedTabName);
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg">
      <div className="p-1 bg-purple-500"></div>
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-700 mb-4">Update Sheet Configuration</h3>
        
        {/* Current Configuration Display */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4 border border-gray-200">
          <h4 className="text-sm font-medium text-gray-600 mb-2">Current Configuration</h4>
          <div className="flex flex-col space-y-1 text-sm">
            <div className="flex items-center">
              <span className="font-medium mr-2 text-gray-700">Sheet ID:</span>
              <span className="text-gray-600 truncate">{currentConfig.sheetId || 'Not set'}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-2 text-gray-700">Tab Name:</span>
              <span className="text-gray-600">{currentConfig.tabName || 'Not set'}</span>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleUpdateConfig} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">New Sheet ID</label>
            <input
              type="text"
              value={newSheetId}
              onChange={(e) => setNewSheetId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-200"
              placeholder="e.g., 16zJ1PrirmRkZDsDttEAPySL4Ez2c-DHA5t2iWVy6V7A"
            />
            <p className="mt-1 text-xs text-gray-500">Enter the ID of your new Google Sheet</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              New Tab Name
              {suggestedTabName && (
                <button
                  type="button"
                  onClick={handleAutoFillTabName}
                  className="ml-2 text-xs text-purple-600 hover:text-purple-800 font-medium"
                >
                  Use Suggested: {suggestedTabName}
                </button>
              )}
            </label>
            <input
              type="text"
              value={newTabName}
              onChange={(e) => setNewTabName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-200"
              placeholder="e.g., Expenses May"
            />
            <p className="mt-1 text-xs text-gray-500">
              The tab name in your new sheet (e.g., "Expenses May")
            </p>
          </div>
          
          <button 
            type="submit" 
            disabled={isLoading || !newSheetId || !newTabName}
            className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg 
            transition-colors duration-200 flex justify-center items-center disabled:bg-purple-300 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </>
            ) : "Update Sheet Configuration"}
          </button>
        </form>
        
        {/* Status Messages */}
        {message && (
          <div className={`mt-4 p-3 rounded-lg ${message.includes('Error') || message.includes('failed') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'} transition-all duration-300 animate-fadeIn shadow-sm`}>
            <p className="text-sm font-medium">{message}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SheetConfigUpdater;